import { LinearProgress } from '@mui/material';
import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';

import ErrorBoundaryWrapper from '@pages/error-boundary.page';
import { useAppSelector } from '@store/index';
//import { appMode } from '@utils/url';
import { ActiveProperties } from '@store/slices/property.slice';

const PointOfSale = lazy(() => import('@modules/point-of-sale'));
const PointOfSaleAdmin = lazy(
  () => import('@modules/point-of-sale/partials/pos-admin-router.partial'),
);

const PointOfSaleBOAdmin = lazy(
  () =>
    import('../../modules/point-of-sale/partials/pos-bo-admin-router.partial'),
);
const PosLayout = lazy(() => import('../layout/pos-layout'));

const Layout = lazy(() => import('@shared/layout/layout'));
const Protected = lazy(() => import('./protected'));
const LoginLayout = lazy(() => import('@shared/layout/login-layout'));
const SubscriptionLayout = lazy(
  () => import('@shared/layout/subscription-layout'),
);
const DashboardLayout = lazy(() => import('@shared/layout/dashboard-layout'));

const UserRegistration = lazy(
  () => import('@pages/user-registration/user-registration.page'),
);
const UserAuthentication = lazy(
  () => import('@pages/user-registration/user-authentication.page'),
);

const OTP = lazy(() => import('@pages/login/otp.page'));
const Dashboard = lazy(() => import('@pages/dashboard/dashboard.page'));
const NotFound = lazy(() => import('@pages/not-found.page'));
const UsersManagement = lazy(() => import('@modules/users-management'));
const FrontOffice = lazy(() => import('@modules/front-office'));
// const AdministrationSettings = lazy(
//   () => import('@modules/administration-settings'),
// );
const AccountingAndFinance = lazy(
  () => import('@modules/accounting-and-finance'),
);
const Login = lazy(() => import('@pages/login/login.page'));
const ForgotPassword = lazy(() => import('@pages/login/forgot-password.page'));
const ValidateCookies = lazy(() => import('@pages/login/validate-cookies'));

const CreateNewPassword = lazy(
  () => import('@pages/login/create-new-password.page'),
);

const DataPrivacyPolicy = lazy(() => import('@components/policy/data-privacy'));

const TermsAndConditions = lazy(
  () => import('@components/policy/terms-and-conditions'),
);

const EndUserLicenseAgreement = lazy(
  () => import('@components/policy/end-user-license-agreement'),
);

const Subscription = lazy(() => import('@pages/subscription'));

const PaymentSuccess = lazy(
  () => import('@pages/payment-gateway/payment-gateway-test-success'),
);
const PaymentError = lazy(
  () => import('@pages/payment-gateway/payment-gateway-test-error'),
);

const WrapErrorBoundary = ({
  component,
  link,
}: {
  component: any;
  link?: string;
}) => {
  const activeProperties = useAppSelector(
    (state) => state.properties.activeProperties,
  );

  if (link) {
    return (
      <ErrorBoundaryWrapper>
        {validateRoute(link, activeProperties) !== -1 ? (
          component
        ) : (
          <NotFound />
        )}
      </ErrorBoundaryWrapper>
    );
  }
  return <ErrorBoundaryWrapper>{component}</ErrorBoundaryWrapper>;
};

const validateRoute = (link: string, activeProperties: ActiveProperties[]) => {
  return activeProperties?.findIndex(
    (property: ActiveProperties) =>
      (link?.includes(property?.link?.split('/')?.[1]) ||
        property?.link == link) &&
      property?.isActive &&
      !property?.disableOnAllProperty,
  );
};

const Router = () => {
  return (
    <Suspense fallback={<LinearProgress />}>
      <Routes>
        <Route
          path='/'
          element={<WrapErrorBoundary component={<Protected />} />}
        >
          {/* MODULES PAGE */}
          <Route
            path='/'
            element={<WrapErrorBoundary component={<DashboardLayout />} />}
          >
            <Route
              path='/'
              element={<WrapErrorBoundary component={<Dashboard />} />}
            />
          </Route>

          {/* USERS MANAGEMENT PAGE */}
          <Route
            path='/'
            element={
              <WrapErrorBoundary
                component={<Layout />}
                link='/users-management'
              />
            }
          >
            <Route
              path='/users-management/*'
              element={<WrapErrorBoundary component={<UsersManagement />} />}
            />
          </Route>

          {/* FRONT OFFICE PAGE */}
          <Route
            path='/'
            element={
              <WrapErrorBoundary component={<Layout />} link='/front-office' />
            }
          >
            <Route
              path='/front-office/*'
              element={<WrapErrorBoundary component={<FrontOffice />} />}
            />
          </Route>

          {/* ADMINISTRATION SETTINGS PAGE */}
          {/* <Route
            path='/'
            element={
              <WrapErrorBoundary
                component={<Layout />}
                link='/administration-settings'
              />
            }
          >
            <Route
              path='/administration-settings/*'
              element={
                <WrapErrorBoundary component={<AdministrationSettings />} />
              }
            />
          </Route> */}

          {/* ACCOUNTING AND FINANCE PAGE */}
          <Route
            path='/'
            element={
              <WrapErrorBoundary
                component={<Layout />}
                link='/accounting-and-finance'
              />
            }
          >
            <Route
              path='/accounting-and-finance/*'
              element={
                <WrapErrorBoundary component={<AccountingAndFinance />} />
              }
            />
          </Route>

          {/* POINT OF SALE */}
          <Route
            path='/'
            element={
              <WrapErrorBoundary
                component={<PosLayout />}
                link='/point-of-sale/selection'
              />
            }
          >
            <Route
              path='/point-of-sale/selection/*'
              element={<WrapErrorBoundary component={<PointOfSale />} />}
            />
          </Route>

          <Route
            path='/'
            element={
              <WrapErrorBoundary
                component={<Layout {...{ virtualKeyboard: true }} />}
                link='/point-of-sale'
              />
            }
          >
            <Route
              path='/point-of-sale/*'
              element={<WrapErrorBoundary component={<PointOfSaleAdmin />} />}
            />
          </Route>

          <Route
            path='/'
            element={
              <WrapErrorBoundary component={<Layout />} link='/back-office' />
            }
          >
            <Route
              path='/back-office/*'
              element={<WrapErrorBoundary component={<PointOfSaleBOAdmin />} />}
            />
          </Route>

          {/* ADD OTHER MODULE ROUTE BELOW */}
        </Route>

        <Route
          path='/'
          element={<WrapErrorBoundary component={<LoginLayout />} />}
        >
          <Route
            path='/login'
            element={<WrapErrorBoundary component={<Login />} />}
          />
          <Route
            path='/forgot-password'
            element={<WrapErrorBoundary component={<ForgotPassword />} />}
          />
          <Route
            path='/otp-verification'
            element={<WrapErrorBoundary component={<OTP />} />}
          />
          <Route
            path='/user-registration/:token'
            element={<UserRegistration />}
          />
          <Route
            path='/user-authentication/:token'
            element={<UserAuthentication />}
          />
          <Route
            path='/create-new-password/:token'
            element={<CreateNewPassword />}
          />
        </Route>

        <Route
          path='/'
          element={<WrapErrorBoundary component={<SubscriptionLayout />} />}
        >
          <Route
            path='/subscriptions'
            element={<WrapErrorBoundary component={<Subscription />} />}
          />
          <Route
            path='/subscriptions/:state'
            element={<WrapErrorBoundary component={<Subscription />} />}
          />
          <Route
            path='/invoices/:state'
            element={<WrapErrorBoundary component={<Subscription />} />}
          />
        </Route>

        <Route
          path='/validate-cookies'
          element={<WrapErrorBoundary component={<ValidateCookies />} />}
        />
        <Route path='/data-privacy-policy' element={<DataPrivacyPolicy />} />
        <Route path='/terms-and-conditions' element={<TermsAndConditions />} />
        <Route
          path='/end-user-license-agreement'
          element={<EndUserLicenseAgreement />}
        />
        <Route path='/payment-gateway-success' element={<PaymentSuccess />} />
        <Route path='/payment-gateway-error' element={<PaymentError />} />

        <Route path='*' element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
